<script lang="ts">
import {defineComponent} from 'vue';
import type {Container} from '../container';

const {assetUrlPrefix} = window.config;

export default defineComponent({
  name: 'AllSpiceBillingSuccess',
  inject: {
    untypedContainer: {from: 'container'},
  },
  props: {
    setupIntent: {
      type: String,
      required: true,
      default: '',
    },
    ownerEmail: {
      type: String,
      required: true,
      default: '',
    },
    billingEmail: {
      type: String,
      required: true,
      default: '',
    },
    csrf: {
      type: String,
      required: true,
      default: '',
    },
    billingPortalLink: {
      type: String,
      required: true,
      default: '',
    },
  },
  data() {
    return {
      imgURL: `${assetUrlPrefix}/img/logo_text.svg`,
      emailSent: false,
      emailSentSucceeded: false,
    };
  },
  computed: {
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
  },
  methods: {
    resendEmail() {
      this.emailSent = true;

      return this.container.lookup('fetcher').fetch(`${window.location.origin}/register/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: `${encodeURIComponent('_csrf')}=${encodeURIComponent(this.csrf)}&${encodeURIComponent('setup_intent')}=${encodeURIComponent(this.setupIntent)}`,
      // eslint-disable-next-line github/no-then
      }).then(() => {
        this.emailSentSucceeded = true;
      // eslint-disable-next-line github/no-then
      }).catch(() => {
        this.emailSent = false;
      });
    },
  },
});
</script>

<template>
  <div class="ui success">
    <img
      class="logo"
      width="500"
      :src="imgURL"
      alt="AllSpice logo"
    >
    <div class="ui grid">
      <div class="success-header">
        <h2 style="font-size: 40px;">
          Thank you for your subscription!
        </h2>
        <h2 style="padding-bottom: 20px">
          We're here to improve the success of your product development process
        </h2>
        <span>
          Login and training instructions will be sent to {{ ownerEmail }} <br>
          Didn't get it? Email isn't heavy; <a
            href="javascript:void(0)"
            :class="{ 'email-disabled' : emailSent }"
            @click="resendEmail"
          >we can send it again.</a>
          <svg
            class="email-sent"
            :class="{ hidden: !emailSentSucceeded }"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="28"
            height="28"
          >
            <path
              fill-rule="evenodd"
              d="M21.03 5.72a.75.75 0 010 1.06l-11.5 11.5a.75.75 0 01-1.072-.012l-5.5-5.75a.75.75 0 111.084-1.036l4.97 5.195L19.97 5.72a.75.75 0 011.06 0z"
            />
          </svg>
        </span>
      </div>
      <div class="success-contents">
        <div class="section">
          <div class="section-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="96"
              height="96"
            >
              <path
                fill-rule="evenodd"
                d="M12.292 2.06a.75.75 0 00-.584 0L.458 6.81a.75.75 0 000 1.38L4.25 9.793v3.803a2.901 2.901 0 00-1.327.757c-.579.58-.923 1.41-.923 2.43v4.5c0 .248.128.486.335.624.06.04.117.073.22.124.124.062.297.138.52.213.448.149 1.09.288 1.925.288s1.477-.14 1.925-.288c.223-.075.396-.15.52-.213a2.11 2.11 0 00.21-.117A.762.762 0 008 21.28v-4.5c0-1.018-.344-1.85-.923-2.428a2.9 2.9 0 00-1.327-.758v-3.17l5.958 2.516a.75.75 0 00.584 0l5.208-2.2v4.003a2.552 2.552 0 01-.079.085 4.057 4.057 0 01-.849.65c-.826.488-2.255 1.021-4.572 1.021-.612 0-1.162-.037-1.654-.1a.75.75 0 00-.192 1.487c.56.072 1.173.113 1.846.113 2.558 0 4.254-.592 5.334-1.23.538-.316.914-.64 1.163-.896a2.84 2.84 0 00.392-.482h.001A.75.75 0 0019 15v-4.892l4.542-1.917a.75.75 0 000-1.382l-11.25-4.75zM5 15c-.377 0-.745.141-1.017.413-.265.265-.483.7-.483 1.368v4.022c.299.105.797.228 1.5.228s1.201-.123 1.5-.228V16.78c0-.669-.218-1.103-.483-1.368A1.431 1.431 0 005 15zm7-3.564L2.678 7.5 12 3.564 21.322 7.5 12 11.436z"
              />
            </svg>
          </div>
          <div class="section-content">
            <div class="section-header">
              <h2>Learn more</h2>
            </div>
            <div class="section-body">
              <span>Visit our knowledgebase for
                <a
                  target="_blank"
                  href="https://allspice.document360.io/docs/getting-started"
                >getting started guides</a>,
                <a
                  target="_blank"
                  href="https://allspice.document360.io/docs/how-to-guides"
                >how-to-guides</a>, and
                <a
                  target="_blank"
                  href="https://allspice.document360.io/docs/faqs"
                >frequently asked questions.</a></span>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="section-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="96"
              height="96"
            >
              <path d="M9.197 10a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm-2.382 4a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm-1.581 4a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z" />
              <path
                fill-rule="evenodd"
                d="M4.125 0h15.75A4.113 4.113 0 0124 4.125c0 1.384-.476 2.794-1.128 4.16-.652 1.365-1.515 2.757-2.352 4.104l-.008.013c-.849 1.368-1.669 2.691-2.28 3.97-.614 1.283-.982 2.45-.982 3.503a2.625 2.625 0 104.083-2.183.75.75 0 01.834-1.247A4.125 4.125 0 0119.875 24H4.5a4.125 4.125 0 01-4.125-4.125c0-2.234 1.258-4.656 2.59-6.902.348-.586.702-1.162 1.05-1.728.8-1.304 1.567-2.553 2.144-3.738H3.39c-.823 0-1.886-.193-2.567-1.035A3.647 3.647 0 010 4.125 4.125 4.125 0 014.125 0zM1.5 4.125A2.625 2.625 0 014.125 1.5h12.568a4.108 4.108 0 00-.943 2.625c0 .723.185 1.349.499 1.882H3.389c-.662 0-1.148-.166-1.402-.479A2.148 2.148 0 011.5 4.125zm14.25 15.75c0 .997.354 1.912.943 2.625H4.5a2.625 2.625 0 01-2.625-2.625c0-1.766 1.025-3.85 2.38-6.137.314-.529.646-1.068.98-1.612.953-1.55 1.927-3.136 2.577-4.619H18a.75.75 0 00.417-1.373c-.746-.5-1.167-1.144-1.167-2.009A2.625 2.625 0 0119.875 1.5 2.613 2.613 0 0122.5 4.125c0 1.057-.368 2.229-.982 3.514-.611 1.28-1.431 2.605-2.28 3.972l-.017.028c-.834 1.343-1.694 2.728-2.343 4.086-.652 1.364-1.128 2.77-1.128 4.15z"
              />
            </svg>
          </div>
          <div class="section-content">
            <div class="section-header">
              <h2>Billing</h2>
            </div>
            <div class="section-body">
              <span>Head on over to our
                <a
                  target="_blank"
                  :href="billingPortalLink"
                >billing portal</a>
                to make adjustments to your payment method or contact information. You should have received a welcome billing email at {{ billingEmail }} </span>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="section-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="96"
              height="96"
            >
              <path d="M8.456 14.494a.75.75 0 011.068.17 3.08 3.08 0 00.572.492A3.381 3.381 0 0012 15.72c.855 0 1.487-.283 1.904-.562a3.081 3.081 0 00.572-.492l.021-.026a.75.75 0 011.197.905l-.027.034c-.013.016-.03.038-.052.063-.044.05-.105.119-.184.198a4.569 4.569 0 01-.695.566A4.88 4.88 0 0112 17.22a4.88 4.88 0 01-2.736-.814 4.57 4.57 0 01-.695-.566 3.253 3.253 0 01-.236-.261c-.259-.332-.223-.824.123-1.084z" />
              <path
                fill-rule="evenodd"
                d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zM2.5 12a9.5 9.5 0 1119 0 9.5 9.5 0 01-19 0z"
              />
              <path d="M9 10.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0zM16.25 12a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z" />
            </svg>
          </div>
          <div class="section-content">
            <div class="section-header">
              <h2>We want you to be happy!</h2>
            </div>
            <div class="section-body">
              <span>
                We’d love to hear from you. Sign up for a
                <a
                  target="_blank"
                  href="https://meetings.hubspot.com/daniel-lindmark/onboarding"
                >one-on-one check-in</a>,
                <a
                  target="_blank"
                  href="https://docs.google.com/forms/d/e/1FAIpQLScVZLtrAd-x9NKeq8dWmb2gJ0qxTbyROEdPY9X4ofki8W-Dbw/viewform?usp=sf_link"
                >submit product feedback</a>, or email us directly at
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="mailto:support@allspice.io"
                >support@allspice.io</a>.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.logo {
  display: block;
  margin-left: auto;
  margin-right: auto;
}
.success {
  width: 50%;
  padding: 20px;
  margin: auto;
}
.success span {
  font-size: 20px;
  line-height: 150%;
}
.success-header {
  text-align: center;
  display: block;
  padding-bottom: 20px;
}
.success-contents {
  padding: 25px;
  margin: 10px;
}
.section {
  display: grid;
  grid-template-columns: 96px 1fr;
  padding-bottom: 45px;
}
.section-icon {
  padding: 5px;
}
.section-icon svg {
  fill: var(--color-secondary-dark-6);
  vertical-align: middle;
}
.section-content {
  padding-left: 40px;
}
.section-header {
  font-size: 18px;
  padding-bottom: 10px;
}
.section-header h2 {
  font-size: 24px;
  padding-bottom: 12px;
}
.section-header span {
  padding-bottom: 10px;
  font-weight: var(--font-weight-semibold);
  display: inline;
}
.section-body {
  display: block;
  padding-right: 15px;
}
.email-sent {
  color: var(--color-primary);
  fill: var(--color-primary);
  padding-top: 8px;
}
.hidden {
  visibility: hidden;
}
.email-disabled {
  pointer-events: none;
  opacity: .5;
}
@media only screen and (max-width: 850px) {
  .success {
    width: 100%;
  }
}
</style>
